import { useState, useEffect, useRef, useCallback } from "react";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const COMMAND_TEXT = "$ spectree run --check";
const CHAR_DELAY = 35; // ms per character typed
const LINE_DELAY = 90; // ms between each line appearing

interface UseTerminalAnimationReturn {
  showBackdrop: boolean;
  showTerminal: boolean;
  typedCommand: string;
  commandDone: boolean;
  /** Number of output lines visible (0 = none, increments line by line) */
  visibleLines: number;
  /** True once all lines are done */
  allDone: boolean;
  prefersReducedMotion: boolean;
  skipAnimation: () => void;
}

const TOTAL_LINES = 11;

export function useTerminalAnimation(): UseTerminalAnimationReturn {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  });
  const [showBackdrop, setShowBackdrop] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const [typedCommand, setTypedCommand] = useState("");
  const [commandDone, setCommandDone] = useState(false);
  const [visibleLines, setVisibleLines] = useState(0);
  const [skipped, setSkipped] = useState(false);

  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const handler = (e: MediaQueryListEvent) => { setPrefersReducedMotion(e.matches); };
    mq.addEventListener("change", handler);
    return () => { mq.removeEventListener("change", handler); };
  }, []);

  useEffect(() => {
    return () => { timeoutsRef.current.forEach(clearTimeout); };
  }, []);

  const showEverything = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setShowBackdrop(true);
    setShowTerminal(true);
    setTypedCommand(COMMAND_TEXT);
    setCommandDone(true);
    setVisibleLines(TOTAL_LINES);
    setSkipped(true);
  }, []);

  const skipAnimation = useCallback(() => { showEverything(); }, [showEverything]);

  useEffect(() => {
    if (prefersReducedMotion) { showEverything(); }
  }, [prefersReducedMotion, showEverything]);

  useEffect(() => {
    if (prefersReducedMotion || skipped) return;

    const push = (t: ReturnType<typeof setTimeout>) => { timeoutsRef.current.push(t); };

    // Phase 1: Backdrop
    push(setTimeout(() => { setShowBackdrop(true); }, 0));

    // Phase 2: Terminal slides up
    push(setTimeout(() => { setShowTerminal(true); }, 300));

    // Phase 3: Type command char by char
    const typeStart = 900;
    for (let i = 0; i <= COMMAND_TEXT.length; i++) {
      push(setTimeout(() => {
        setTypedCommand(COMMAND_TEXT.slice(0, i));
        if (i === COMMAND_TEXT.length) setCommandDone(true);
      }, typeStart + i * CHAR_DELAY));
    }

    // Phase 4: After typing + 1s pause, print lines one by one
    const lineStart = typeStart + COMMAND_TEXT.length * CHAR_DELAY + 1000;
    for (let line = 1; line <= TOTAL_LINES; line++) {
      push(setTimeout(() => { setVisibleLines(line); }, lineStart + line * LINE_DELAY));
    }

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [prefersReducedMotion, skipped, showEverything]);

  return {
    showBackdrop,
    showTerminal,
    typedCommand,
    commandDone,
    visibleLines,
    allDone: visibleLines >= TOTAL_LINES,
    prefersReducedMotion,
    skipAnimation,
  };
}
